<template>
  <v-container v-if="initialPlanning?.id" class="bg-surface pa-0" fluid>
    <!-- Versions Comparative Header -->
    <div class="pt-4 px-4">
      <div class="text-h6 font-weight-bold text-primary text-center mb-2">
        {{ finalPlanning?.id && finalPlanning.getTitle() }}
      </div>
      <div class="d-flex align-center justify-center">
        <div style="width: 320px">
          <v-text-field :model-value="initialPlanningVersionName" readonly density="compact"></v-text-field>
        </div>
        <v-icon class="mx-2">far fa-long-arrow-alt-right</v-icon>
        <div style="width: 320px">
          <select-filter v-model="finalProject" :items="savedProjectVersions" :item-title="getItemText"
                         item-value="id" return-object
                         @update:model-value="loadFinalProjectVersion">
          </select-filter>
        </div>
      </div>
    </div>

    <div class="d-flex align-end px-4 my-2">
      <!-- Group By Lane -->
      <div class="d-flex gap-4">
        <v-switch v-model="groupByLane" :label="$t('PLANNING_VERSIONS.GROUP_BY_LANE')"></v-switch>
        <v-switch v-model="displayAll" :label="$t('PLANNING_VERSIONS.DISPLAY_ALL_ELEMENTS')"></v-switch>
      </div>
    </div>

    <!-- Table Comparative -->
    <div v-if="! dataLoaded.finalVersion" class="text-center ma-6"><i class="far fa-spinner fa-spin fa-2x fa-fw"></i></div>
    <v-data-table v-else :headers="headers" :items="tableItems" :sort-by="[{ key: 'finalstarttime', order: 'asc' }]" :group-by="groupByLane ? [{ key: 'laneId', order: 'asc' }] : []"
                  fixed-header height="calc(100vh - 96px)" hide-default-footer>
      <template #header.data-table-group>
        <!-- toggle all lanes header -->
        <v-btn-icon v-if="groupByLane" size="x-small" class="my-n2" @click="allGroupsState = allGroupsState == 'opened' ? 'closed' : 'opened'">
          <v-icon class="export-hidden">far {{ allGroupsState == 'opened' ? 'fa-chevron-down' : 'fa-chevron-right' }}</v-icon>
        </v-btn-icon>
      </template>
      <template #group-header="{ item, columns, isGroupOpen, toggleGroup }">
        <!-- toggle lane -->
        <tr :title="isGroupOpen(item) ? $t('PLANNING.HIDE_LANE') : $t('PLANNING.SHOW_LANE')" class="pointer" @click="allGroupsState = null; toggleGroup(item)">
          <th :colspan="columns.length" style="background: rgb(var(--v-theme-verylightgrey))">
            <v-icon class="export-hidden mr-2">far {{ isGroupOpen(item) ? 'fa-chevron-down' : 'fa-chevron-right' }}</v-icon>
            <span class="vcenter">{{ item.items[0].raw.laneLabel }}</span>
          </th>
          <!-- trick to force open/close all groups when rerendering -->
          {{ allGroupsState == 'opened' && ! isGroupOpen(item) ? toggleGroup(item) : '' }}
          {{ allGroupsState == 'closed' && isGroupOpen(item) ? toggleGroup(item) : '' }}
        </tr>
      </template>
      <template #item="{ item }">
        <tr>
          <td v-for="col in headers" :key="col.key" class="px-2">
            <template v-if="col.key == 'data-table-group'">
              <div v-if="item.icon.name" class="text-center">
                <v-icon :color="item.icon.color" style="margin-bottom: 1px">{{ item.icon.name }}</v-icon>
              </div>
            </template>
            <template v-if="col.key == 'title'">
              <div style="min-width: 150px">
                <v-icon v-if="item.new" color="successgreen" :title="$t('PLANNING_VERSIONS.NEW_ELEMENT')" :size="10" style="margin: 0 6px 2px 0">fas fa-circle</v-icon>
                <span>{{ item.title }}</span>
              </div>
            </template>
            <template v-else-if="col.key == 'users'">
              <div class="d-flex" style="flex-wrap: wrap">
                <div v-for="user in item.usersData" style="margin-right: 2px">
                  <v-tooltip location="left">
                    <template #activator="{ props }">
                      <img v-if="user.avatar" :src="user.avatar" alt=" " style="max-height: 20px; min-height: 20px; min-width: 20px; border-radius: 50%;" v-bind="props">
                      <i v-else class="far fa-user" style="margin: 0 3px" v-bind="props"></i>
                    </template>
                    <span>{{ $filters.username(user) }}</span>
                  </v-tooltip>
                </div>
              </div>
            </template>
            <template v-if="col.key == 'initialstarttime'">
              <div v-if="item.initialstarttime" class="my-1 nobr">{{ $filters.moment(item.initialstarttime, 'll') }}</div>
            </template>
            <template v-if="col.key == 'finalstarttime'">
              <div v-if="item.finalstarttime" class="my-1 nobr">
                {{ $filters.moment(item.finalstarttime, 'll') }}
              </div>
            </template>
            <template v-if="col.key == 'diffstarttimes'">
              <v-chip v-if="item.diffstarttimes" :color="item.diffstarttimes > 0 ? 'errorred' : 'successgreen'">
                <span class="text-ellipsis">{{ item.diffstarttimes > 0 ? `+${item.diffstarttimes}` : item.diffstarttimes }} {{ $t('GLOBAL.DAYS', item.diffstarttimes) }}</span>
              </v-chip>
            </template>
            <template v-if="col.key == 'initialendtime'">
              <div v-if="item.initialendtime" class="my-1 nobr">{{ $filters.moment(item.initialendtime, 'll') }}</div>
            </template>
            <template v-if="col.key == 'finalendtime'">
              <div v-if="item.finalendtime" class="my-1 nobr">{{ $filters.moment(item.finalendtime, 'll') }}</div>
            </template>
            <template v-if="col.key == 'diffendtimes'">
              <v-chip v-if="item.diffendtimes" :color="item.diffendtimes > 0 ? 'errorred' : 'successgreen'">
                <span class="text-ellipsis">{{ item.diffendtimes > 0 ? `+${item.diffendtimes}` : item.diffendtimes }} {{ $t('GLOBAL.DAYS', item.diffendtimes) }}</span>
              </v-chip>
            </template>
            <template v-if="col.key == 'diffdurations'">
              <v-chip v-if="item.diffdurations" :color="item.diffdurations > 0 ? 'errorred' : 'successgreen'">
                <span class="text-ellipsis">{{ item.diffdurations > 0 ? `+${item.diffdurations}` : item.diffdurations }} {{ $t('GLOBAL.DAYS', item.diffdurations) }}</span>
              </v-chip>
            </template>
            <template v-else-if="col.key == 'progress'">
              <div v-if="item.progress || item.initialProgress" class="d-flex align-center">
                {{ $filters.number(item.initialProgress) }}%
                <v-icon class="mx-1">far fa-long-arrow-alt-right</v-icon>
                <span>{{ $filters.number(item.progress) }}%</span>
              </div>
            </template>
            <template v-else-if="col.key == 'checklist'">
              <div v-if="item.checklist" class="d-flex align-center" style="min-width: 100px">
                <span>{{ item.initialChecklist || 0 }} / {{ item.checklist }}</span>
                <v-icon class="mx-1">far fa-long-arrow-alt-right</v-icon>
                <span>{{ item.checklist }} / {{ item.checklist }}</span>
              </div>
            </template>
            <template v-else-if="col.key == 'budget'">
              <div v-if="item.initialBudgetData || item.budgetData" class="d-flex align-center">
                <div v-if="item.initialBudgetData">
                  <div v-for="(initialBudget, icon) in item.initialBudgetData" :key="`version-${icon}`" class="nobr text-ellipsis">
                    <span :class="`budget-${icon}`"></span>
                    <span v-if="initialBudget.amount_inprogress && initialBudget.amount">{{ $filters.number(initialBudget.amount_inprogress) }}&nbsp;/</span>
                    <span v-if="initialBudget.amount_inprogress && ! initialBudget.amount">{{ $filters.number(initialBudget.amount_inprogress) }}</span>
                    <span v-if="initialBudget.amount">{{ $filters.number(initialBudget.amount) }}</span>
                  </div>
                </div>
                <div v-else>0</div>
                <v-icon class="mx-1">far fa-long-arrow-alt-right</v-icon>
                <div v-if="item.budgetData">
                  <div v-for="(finalBudget, icon) in item.budgetData" :key="icon" class="nobr text-ellipsis">
                    <span :class="`budget-${icon}`"></span>
                    <span v-if="finalBudget.amount_inprogress && finalBudget.amount">{{ $filters.number(finalBudget.amount_inprogress) }}&nbsp;/</span>
                    <span v-if="finalBudget.amount_inprogress && ! finalBudget.amount">{{ $filters.number(finalBudget.amount_inprogress) }}</span>
                    <span v-if="finalBudget.amount">{{ $filters.number(finalBudget.amount) }}</span>
                  </div>
                </div>
                <div v-else>0</div>
              </div>
            </template>
          </td>
        </tr>
      </template>
    </v-data-table>
  </v-container>
  <v-card v-else class="pa-6 ma-1">
    <span>{{ $t('PLANNING_VERSIONS.NO_VERSION_SELECTED') }}</span>
  </v-card>
</template>

<script>
  import useUsersStore from '@/stores/users';
  import Planning from '@/models/Planning';
  import useUiMsgboxStore from '@/stores/ui/msgbox';
  import ProjectSrv from '@/components/Projects/ProjectSrv';
  import usePlanningLanesStore from '@/stores/planning/lanes';

  export default {
    props: {
      planning: { type: Object, required: true },
      initialPlanning: { type: Object, default: null },
      projectVersionsList: { type: Array, default: () => [] },
    },
    data() {
      return {
        groupByLane: true,
        displayAll: false,
        defaultProjectVersion: { date_of_modification: this.planning.meta.date_of_modification, version_name: this.$t('PLANNING_VERSIONS.CURRENT_VERSION') },
        finalProject: null,
        finalPlanning: null,
        planningSavedVersionsCache: {},
        dataLoaded: { finalVersion: true },
        headers: [
          { title: '', key: 'data-table-group', width: '0px' },
          { title: this.$t('ELEMENT-DETAIL.TITLE'), key: 'title', width: '0px' },
          { title: this.$t('ELEMENT-DETAIL.USERS'), key: 'users', width: '0px' },
          { title: this.$t('PLANNING_VERSIONS.INITIAL_STARTTIME'), key: 'initialstarttime', width: '0px' },
          { title: this.$t('PLANNING_VERSIONS.FINAL_STARTTIME'), key: 'finalstarttime', width: '0px' },
          { title: this.$t('PLANNING_VERSIONS.ECART'), key: 'diffstarttimes', width: '0px' },
          { title: this.$t('PLANNING_VERSIONS.INITIAL_ENDTIME'), key: 'initialendtime', width: '0px' },
          { title: this.$t('PLANNING_VERSIONS.FINAL_ENDTIME'), key: 'finalendtime', width: '0px' },
          { title: this.$t('PLANNING_VERSIONS.ECART'), key: 'diffendtimes', width: '0px' },
          { title: this.$t('PLANNING_VERSIONS.DURATION'), key: 'diffdurations', width: '0px' },
          { title: this.$t('ELEMENT-DETAIL.PROGRESS'), key: 'progress', width: '0px' },
          { title: this.$t('ELEMENT-DETAIL.CHECKLIST'), key: 'checklist', width: '0px' },
          { title: this.$t('ELEMENT-DETAIL.BUDGET'), key: 'budget', width: '0px' },
        ],
        allGroupsState: null,
        uiMsgboxStore: useUiMsgboxStore(),
        planningLanesStore: usePlanningLanesStore(),
      };
    },
    computed: {
      allItems() {
        const items = [];
        (this.finalPlanning?.lanes || []).forEach((lane) => {
          const laneElements = this.planningLanesStore.getLaneElements({ planning: this.finalPlanning, laneId: lane.id });
          laneElements.forEach((el) => {
            const initialEl = this.initialPlanning?.elements.find(item => item.id == el.id);
            const initialBudgetData = this.budget(initialEl);
            const budgetData = this.budget(el);
            const usersData = this.users(el);
            const [elStart, elEnd, initialElStart, initialElEnd] = [el.getStartTime(), el.getEndTime(), initialEl?.getStartTime(), initialEl?.getEndTime()];
            const item = {
              laneId: lane.id,
              laneLabel: lane.label,
              new: ! initialEl,
              title: el.getTitle() || this.elDefaultTitle(el),
              icon: el.hasIcon() && el.getIcon(),
              users: usersData ? Object.keys(usersData).length : null,
              usersData,
              initialstarttime: initialElStart?.format('YYYY-MM-DD') || null,
              finalstarttime: elStart?.format('YYYY-MM-DD') || null,
              diffstarttimes: elStart && initialElStart ? elStart.diff(initialElStart, 'days') : null,
              initialendtime: initialElEnd?.format('YYYY-MM-DD') || null,
              finalendtime: elEnd?.format('YYYY-MM-DD') || null,
              diffendtimes: elEnd && initialElEnd ? elEnd.diff(initialElEnd, 'days') : null,
              diffdurations: elEnd && elStart && initialElEnd && initialElStart ? (elEnd.diff(elStart, 'days') - initialElEnd.diff(initialElStart, 'days')) : null,
              initialProgress: initialEl?.getProgress() || 0,
              progress: el.getProgress() || 0,
              initialChecklist: initialEl?.getChecklist()?.length || 0,
              checklist: el.getChecklist()?.length || 0,
              initialBudgetData,
              initialBudget: initialBudgetData ? Object.keys(initialBudgetData).reduce((acc, icon) => acc + (initialBudgetData[icon].amount || initialBudgetData[icon].amount_inprogress), 0) : null,
              budgetData,
              budget: budgetData ? Object.keys(budgetData).reduce((acc, icon) => acc + (budgetData[icon].amount || budgetData[icon].amount_inprogress), 0) : null,
            };
            items.push(item);
          });
        });
        return items;
      },
      tableItems() {
        if (this.displayAll) return this.allItems;
        return this.allItems.filter((item) => {
          if (item.diffstarttimes || item.diffendtimes || item.diffdurations) return true;
          if (item.progress != item.initialProgress || item.checklist != item.initialChecklist || item.budget != item.initialBudget) return true;
          return false;
        });
      },
      initialPlanningVersionName() {
        return this.initialPlanning?.meta?.version_name || `${this.$t('PLANNING_VERSIONS.VERSION_OF')} ${moment(this.initialPlanning?.meta?.date_of_modification).format('L')}`;
      },
      savedProjectVersions() {
        const savedVersions = this.projectVersionsList.filter(version => version.version_name && ! version.isLocked);
        savedVersions.sort((a, b) => moment(b?.date_of_modification) - moment(a?.date_of_modification));
        return [this.defaultProjectVersion, ...savedVersions];
      },
    },
    created() {
      this.finalProject = this.defaultProjectVersion;
      this.finalPlanning = this.planning;
    },
    methods: {
      loadFinalProjectVersion() {
        const versionId = this.finalProject?.id;
        if (! versionId) { // current version
          this.finalProject = this.defaultProjectVersion;
          this.finalPlanning = this.planning;
          return;
        }
        if (this.planningSavedVersionsCache[versionId]) { // final version cached
          this.finalPlanning = this.planningSavedVersionsCache[versionId];
          return;
        }
        this.dataLoaded.finalVersion = false;
        ProjectSrv.getProjectVersion(versionId).then((content) => {
          this.finalPlanning = new Planning(content);
          this.finalPlanning.meta.version_name = this.finalProject.version_name;
          this.planningSavedVersionsCache[versionId] = this.finalPlanning;
          this.dataLoaded.finalVersion = true;
        }).catch((message) => {
          console.error(message);
          this.dataLoaded.finalVersion = true;
          if (message) this.uiMsgboxStore.open({ title: "Error : Planning version was not loaded", body: message });
        });
      },
      users(item) {
        const usersStore = useUsersStore();
        if (! item || ! item.getUsers()) return null;
        const users = {};
        item.getUsers().forEach((user) => {
          if (! user.id) return;
          users[user.id] = usersStore.getUserById(user.id) || user;
        });

        return Object.keys(users).map(userId => users[userId]).sort((a, b) => {
          const aUsername = usersStore.getUsername(a);
          const bUsername = usersStore.getUsername(b);
          return (aUsername.toLowerCase() < bUsername.toLowerCase()) ? -1 : 1;
        });
      },
      budget(item) {
        const elBudgets = item && item.getBudgets();
        if (! elBudgets) return null;
        if (! elBudgets.some(budget => budget.amount || budget.amount_inprogress)) return null;

        const budgetByIcons = {};
        (item.getBudgets() || []).forEach((budget) => {
          const icon = budget.icon || '';
          if (! budgetByIcons[icon]) budgetByIcons[icon] = { amount: 0, amount_inprogress: 0 };
          budgetByIcons[icon].amount += +budget.amount || 0;
          budgetByIcons[icon].amount_inprogress += +budget.amount_inprogress || 0;
        });
        return budgetByIcons;
      },
      elDefaultTitle(el) {
        return this.$t('helpers.elDefaultTitle', { type: el.getType() });
      },
      getItemText(item) {
        return `${moment(item.date_of_modification).format('L')} - ${item.version_name}`;
      },
    },
  };
</script>
